/**
 * The profiler itself: rows in, per-field measurements out.
 *
 * One pass per field over the values, no I/O, no clock. Same reasoning as `@dedupr/match-core`:
 * a pure function is the same function in a browser and in a container, so
 * [14-qa-strategy](../../../docs/platform/14-qa-strategy.md)'s wasm32 parity is structural rather
 * than something a comparison harness has to keep proving.
 */
import { classifyPii, inferFormat, profileFormat, words } from './classify.js';
import type { DatasetProfile, FieldProfile } from './model.js';

/** A row as the profiler sees it: whatever the source produced, keyed by column. */
export type ProfileRow = Record<string, unknown>;

/**
 * Present means: not null, not undefined, and not empty after trimming.
 *
 * ⚠️ **`0` and `false` are PRESENT.** A falsy check here would report a column of zeroes as
 * entirely missing, which is the completeness figure being exactly wrong about a column that is
 * completely populated.
 */
function presentValue(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  return text === '' ? null : text;
}

/**
 * How much a field's *semantics* recommend it as a matching key, before its data is considered.
 *
 * Email outranks everything because it is the one field people rarely share and rarely mistype
 * beyond recognition. A free-text column scores 0.3 rather than 0 — an unrecognised custom field is
 * often exactly where a mature CRM keeps its real identifier, and scoring it zero would hide the
 * case this ranking exists to surface.
 */
function semanticWeight(rawField: string, format: string | null): number {
  // Same normalisation as the classifiers: `\b` does not match around an underscore.
  const field = words(rawField);
  if (format === 'email') return 1;
  if (/\b(e-?mail|mail)\b/i.test(field)) return 1;
  if (/\b(name|firstname|lastname|surname|forename|company|organisation|organization|account)\b/i.test(field)) {
    return 0.8;
  }
  if (format === 'phone' || /\b(phone|mobile|tel|cell)\b/i.test(field)) return 0.7;
  /* An external/CRM id is an excellent key when present, but it is usually a *source* id rather
     than a shared identifier — two systems will not agree on it, which is what matching needs. */
  if (/\b(id|ref|reference|number|code)\b/i.test(field)) return 0.5;
  if (format === 'date' || format === 'number') return 0.1;
  return 0.3;
}

/** Profile one field across the dataset. */
export function profileField(field: string, rows: readonly ProfileRow[]): FieldProfile {
  const total = rows.length;
  const values: string[] = [];
  const counts = new Map<string, { value: string; count: number }>();

  for (const row of rows) {
    const value = presentValue(row[field]);
    if (value === null) continue;
    values.push(value);
    // Case-insensitive, because `Ada@x.com` and `ada@x.com` are one value for every purpose a
    // profile serves — counting them as two would overstate distinctness on exactly the columns
    // where distinctness matters most.
    const key = value.toLowerCase();
    const seen = counts.get(key);
    if (seen) seen.count += 1;
    else counts.set(key, { value, count: 1 });
  }

  const present = values.length;
  const distinct = counts.size;
  const format = inferFormat(field, values);
  const { pii, reason } = classifyPii(field, values, format);

  const completeness = total === 0 ? 0 : present / total;
  const distinctness = present === 0 ? 0 : distinct / present;

  const topValues = [...counts.values()]
    .filter((v) => v.count > 1)
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    .slice(0, 5);

  /* ── The identity score ──────────────────────────────────────────────────────────────────
     Three factors multiplied rather than averaged, because each is a veto: a field that is 100%
     complete and always the same value is useless, and averaging would still score it 0.6. */
  const weight = semanticWeight(field, format);
  const identityScore = Number((completeness * distinctness * weight).toFixed(3));

  const reasons: string[] = [];
  if (completeness < 0.5) {
    reasons.push(`Only ${Math.round(completeness * 100)}% of rows have a value, so most records could not be matched on it.`);
  } else if (completeness >= 0.9) {
    reasons.push(`${Math.round(completeness * 100)}% of rows have a value.`);
  }
  if (present > 0 && distinctness < 0.5) {
    /* ⚠️ `topValues[0]` cannot be absent here, and the fallback that used to stand in for it was
       dead code — removed 2026-08-05 after the coverage gate flagged it as the one unreached line
       in this file. The argument is pigeonhole: `topValues` keeps every value whose count exceeds 1,
       and `distinctness < 0.5` means `distinct < present / 2`, so with `present > 0` at least one
       value must repeat. A branch that cannot be reached is worse than no branch — it reads as a
       handled case and is never exercised, so a later change that DOES make it reachable ships
       whatever that untested string happens to say. */
    const dominant = topValues[0]!;
    reasons.push(
      `Values repeat heavily — "${dominant.value}" appears ${dominant.count} times, so it separates records poorly.`
    );
  } else if (distinctness >= 0.95 && present > 0) {
    reasons.push('Almost every value is different, which is what a good matching key looks like.');
  }
  if (weight >= 0.8) reasons.push('This kind of field is normally a reliable way to identify someone.');
  if (weight <= 0.1) reasons.push('This kind of field describes when or how much, not who.');
  if (present === 0) reasons.push('The column is empty, so there is nothing to match on.');

  return {
    field,
    total,
    present,
    completeness: Number(completeness.toFixed(3)),
    distinct,
    distinctness: Number(distinctness.toFixed(3)),
    topValues,
    format: format ? profileFormat(format, values) : null,
    pii,
    piiReason: reason,
    identityScore,
    identityReasons: reasons,
  };
}

/**
 * Profile every column of a dataset.
 *
 * Columns come from the **union of the rows' keys**, not from the first row: a CSV parser that
 * omits trailing empty cells would otherwise hide a column entirely, and the column it hides is
 * disproportionately likely to be the sparse one worth knowing about.
 */
export function profileDataset(rows: readonly ProfileRow[], fields?: readonly string[]): DatasetProfile {
  const columns = fields ? [...fields] : [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const profiles = columns.map((field) => profileField(field, rows));

  /* 0.2 is a floor, not a recommendation: it drops the fields that are plainly unusable (empty,
     constant, or a date) while leaving anything a person might reasonably choose. Ranking a
     hopeless field 7th out of 7 would still put it on the list. */
  const identityCandidates = profiles
    .filter((p) => p.identityScore >= 0.2)
    .sort((a, b) => b.identityScore - a.identityScore)
    .map((p) => p.field);

  return { rows: rows.length, fields: profiles, identityCandidates };
}
